import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { auth, db } from "../firebase";
import { doc, setDoc, getDoc, getDocs, collection, query, where } from "firebase/firestore";
import { updateStreak } from "../utils/streakUtils";
import { examTypes } from "../utils/config";
import { motion, AnimatePresence } from "framer-motion";
import { containsBadWord } from "../utils/badWords";
import { toast } from "sonner";

function GoogleInitialSetupPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId, displayName, photoURL } = location.state || {};
  const [username, setUsername] = useState(displayName || "");
  const [selectedExam, setSelectedExam] = useState("TYT–AYT");
  const [referralCode, setReferralCode] = useState("");
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!userId) navigate("/");
  }, [userId, navigate]);

  const findUserByUsername = async (username) => {
    const q = query(collection(db, "users"), where("username", "==", username));
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      return true;
    }
    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (!privacyAccepted) {
      setError("Lütfen gizlilik politikasını kabul edin.");
      toast.error("Lütfen gizlilik politikasını kabul edin.");
      setLoading(false);
      return;
    }

    if (containsBadWord(username)) {
      setError("Kullanıcı adı uygun olmayan kelimeler içeriyor. Lütfen başka bir kullanıcı adı seçin.");
      toast.error("Kullanıcı adı uygun olmayan kelimeler içeriyor. Lütfen başka bir kullanıcı adı seçin.");
      setLoading(false);
      return;
    }

    if (referralCode && containsBadWord(referralCode)) {
      setError("Referans kodu uygun olmayan kelimeler içeriyor. Lütfen başka bir kod seçin.");
      toast.error("Referans kodu uygun olmayan kelimeler içeriyor.");
      setLoading(false);
      return;
    }

    const userExists = await findUserByUsername(username);
    if (userExists) {
      setError("Bu kullanıcı adı zaten kullanılıyor. Lütfen başka bir kullanıcı adı seçin.");
      toast.error("Bu kullanıcı adı zaten kullanılıyor.");
      setLoading(false);
      return;
    }

    const userRef = doc(db, "users", userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      try {
        const generatedReferralCode = `${username}-${Math.random().toString(36).substr(2, 8)}`;
        await setDoc(userRef, {
          username: username,
          examType: selectedExam,
          photoURL: photoURL || null,
          email: auth.currentUser?.email || "",
          emailVerified: false,
          referralCode: generatedReferralCode,
          referralCodeUsed: referralCode || null,
          isPremium: false,
          createdAt: new Date().toISOString(),
        });
        localStorage.setItem("username", username);
        localStorage.setItem("examType", selectedExam);
        localStorage.setItem("userId", userId);
        await updateStreak(userId, "login");
        toast.success("Profil tamamlandı! Hoş geldiniz!");
        setTimeout(() => navigate("/home", { replace: true }), 300);
      } catch (err) {
        setError("Kayıt sırasında bir hata oluştu: " + err.message);
        toast.error("Kayıt sırasında bir hata oluştu: " + err.message);
        setLoading(false);
      }
    } else {
      navigate("/home", { replace: true });
    }
  };

  return (
    <motion.div
      style={containerStyle}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <motion.div
        style={setupCardStyle}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.5 }}
      >
        <motion.h1
          style={titleStyle}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          Hoş Geldin!
        </motion.h1>

        <AnimatePresence>
          {error && (
            <motion.p
              style={errorStyle}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.3 }}
            >
              {error}
            </motion.p>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {loading && (
            <motion.div
              style={loadingOverlayStyle}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div style={spinnerStyle}></div>
              <p style={loadingTextStyle}>Profil Oluşturuluyor...</p>
            </motion.div>
          )}
        </AnimatePresence>

        <form onSubmit={handleSubmit} style={formStyle}>
          <motion.input
            type="text"
            placeholder="Kullanıcı Adı (Değiştirilemez)"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            style={inputStyle}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            whileFocus={{ borderColor: "#00e676" }}
          />
          <motion.p
            style={warningTextStyle}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            ⚠️ Kullanıcı adı ilerde değiştirilemez, dikkatli seçin!
          </motion.p>
          <motion.select
            value={selectedExam}
            onChange={(e) => setSelectedExam(e.target.value)}
            style={selectStyle}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            whileFocus={{ borderColor: "#00e676" }}
          >
            {examTypes.map((exam, i) => (
              <option key={i} value={exam}>
                {exam}
              </option>
            ))}
          </motion.select>
          <motion.input
            type="text"
            placeholder="Referans Kodu (Opsiyonel, Değiştirilemez)"
            value={referralCode}
            onChange={(e) => setReferralCode(e.target.value)}
            style={inputStyle}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            whileFocus={{ borderColor: "#00e676" }}
          />
          <motion.p
            style={warningTextStyle}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            ⚠️ Referans kodu tercihe bağlıdır, girmenize gerek yoktur!
          </motion.p>
          <motion.div
            style={privacyContainerStyle}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.5 }}
          >
            <motion.input
              type="checkbox"
              id="privacy"
              checked={privacyAccepted}
              onChange={(e) => setPrivacyAccepted(e.target.checked)}
              style={privacyCheckboxStyle}
              whileHover={{ scale: 1.2 }}
              transition={{ duration: 0.2 }}
            />
            <label htmlFor="privacy" style={privacyLabelStyle}>
              <motion.a
                href="/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                style={privacyLinkStyle}
                whileHover={{ color: "#00ffcc" }}
                transition={{ duration: 0.2 }}
              >
                Gizlilik Politikasını
              </motion.a>{" "}
              kabul ediyorum.
            </label>
          </motion.div>
          <motion.button
            type="submit"
            style={submitButtonStyle}
            disabled={!privacyAccepted || loading}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.5 }}
            whileHover={{ scale: privacyAccepted && !loading ? 1.05 : 1 }}
            whileTap={{ scale: privacyAccepted && !loading ? 0.95 : 1 }}
          >
            Profili Tamamla
          </motion.button>
        </form>
      </motion.div>
    </motion.div>
  );
}

const containerStyle = {
  minHeight: "100vh",
  background: "linear-gradient(135deg, #0f2027, #203a43, #2c5364)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: "20px",
};

const setupCardStyle = {
  background: "linear-gradient(145deg, #203a43, #2c5364)",
  padding: "30px",
  borderRadius: "16px",
  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.5)",
  border: "1px solid rgba(0, 230, 118, 0.2)",
  width: "100%",
  maxWidth: "400px",
};

const titleStyle = {
  fontSize: "28px",
  color: "#00e676",
  textAlign: "center",
  marginBottom: "20px",
  fontWeight: "600",
  textShadow: "0 0 8px rgba(0, 230, 118, 0.3)",
};

const errorStyle = {
  color: "#f44336",
  textAlign: "center",
  fontSize: "14px",
  marginBottom: "15px",
};

const loadingOverlayStyle = {
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  background: "rgba(0, 0, 0, 0.7)",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "16px",
};

const spinnerStyle = {
  width: "40px",
  height: "40px",
  border: "4px solid #00e676",
  borderTop: "4px solid transparent",
  borderRadius: "50%",
  animation: "spin 1s linear infinite",
};

const loadingTextStyle = {
  color: "#00e676",
  marginTop: "10px",
  fontSize: "14px",
};

const formStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "15px",
};

const inputStyle = {
  padding: "12px",
  borderRadius: "8px",
  border: "1px solid #2a2a2a",
  background: "rgba(255, 255, 255, 0.15)",
  color: "#fff",
  fontSize: "14px",
  fontWeight: "500",
  width: "100%",
  boxSizing: "border-box",
  outline: "none",
  transition: "all 0.2s ease",
};

const selectStyle = {
  padding: "12px",
  borderRadius: "8px",
  border: "1px solid #2a2a2a",
  background: "rgba(255, 255, 255, 0.15)",
  color: "#fff",
  fontSize: "14px",
  fontWeight: "500",
  width: "100%",
  boxSizing: "border-box",
  outline: "none",
  transition: "all 0.2s ease",
  appearance: "none",
  WebkitAppearance: "none",
  MozAppearance: "none",
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23fff' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right 12px center",
  backgroundSize: "16px",
};

const warningTextStyle = {
  color: "#ffca28",
  fontSize: "12px",
  margin: "-10px 0 10px 0",
  textAlign: "center",
};

const privacyContainerStyle = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  marginTop: "10px",
};

const privacyCheckboxStyle = {
  width: "16px",
  height: "16px",
};

const privacyLabelStyle = {
  fontSize: "14px",
  color: "#ccc",
};

const privacyLinkStyle = {
  color: "#00e676",
  textDecoration: "underline",
};

const submitButtonStyle = {
  background: "linear-gradient(90deg, #00e676, #00c853)",
  color: "#fff",
  border: "none",
  borderRadius: "8px",
  padding: "12px",
  fontSize: "16px",
  fontWeight: "500",
  cursor: "pointer",
  width: "100%",
};

export default GoogleInitialSetupPage;